import {
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import { Box } from '@mui/system';
import axios from 'axios';
import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useForm } from 'react-hook-form';
import { TOKEN_KEY, url } from '../../../../Helpers/utils/Constant';
import Toaster from '../../../../Toaster/Toaster';

interface IProps {
  handleClose: Function;
  setAnnoucements?: Function;
  type?: string | string[];
  setRefreshTrigger: Function;
}

const CreateAnnoucement = ({
  handleClose,
  setRefreshTrigger,
}: IProps) => {
  const {
    register,
    handleSubmit,
    reset,
  } = useForm();
  const Toast = Toaster();
  const [loading, setLoading] = useState<Boolean>(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [thumbnail, setThumbnail] = useState<File | null>(null);
  const [selectedTypes, setSelectedTypes] = useState<string[]>(['NOTICE']);
  const token = localStorage.getItem(TOKEN_KEY);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      const filesArray = Array.from(event.target.files);
      setSelectedFiles((prev) => [...prev, ...filesArray]);
      event.target.value = '';
    }
  };

  const handleTypeToggle = (typeName: string) => {
    setSelectedTypes((prev) =>
      prev.includes(typeName)
        ? prev.filter((t) => t !== typeName)
        : [...prev, typeName]
    );
  };

  const onSubmit = async (inputs: any) => {
    if (selectedTypes.length === 0) {
      return Toast.fire({
        icon: 'error',
        title: 'Please select at least one announcement type',
      });
    }

    if (selectedFiles?.length === 0) {
      return Toast.fire({
        icon: 'error',
        title: `Please select at least one file`,
      });
    }

    setLoading(true);
    try {
      const formData = new FormData();

      formData.append('title', inputs.title);
      formData.append('content', inputs.content);
      if (thumbnail) {
        formData.append('thumbnail', thumbnail);
      }

      selectedTypes.forEach((t) => {
        formData.append('type', t);
      });

      selectedFiles.forEach((file) => {
        formData.append('attachments', file);
      });

      const { data } = await axios.post(
        `${url}/api/v1/admin/announcement`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      if (data.success) {
        Toast.fire({
          icon: 'success',
          title: `${data.message}`,
        });
        setRefreshTrigger((prev: number) => prev + 1);
        setLoading(false);
        handleClose();
      } else {
        Toast.fire({
          icon: 'error',
          title: `${data.message}`,
        });
        setLoading(false);
      }

      reset();
      setSelectedFiles([]);
    } catch (err: any) {
      Toast.fire({
        icon: 'error',
        title: `${err.response?.data?.message || 'Failed to create announcement'}`,
      });
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Atab - Create Announcement</title>
      </Helmet>

      <Box
        onSubmit={handleSubmit(onSubmit)}
        component='form'
        sx={{ p: 2, mt: 1 }}
      >
        <Grid container spacing={2}>
          <Grid xs={12} sm={6} item>
            <TextField
              margin='normal'
              fullWidth
              required
              id='name'
              label='Enter a title'
              {...register('title')}
              type='text'
              autoFocus
              variant='outlined'
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              margin='normal'
              fullWidth
              id='file'
              label='Upload Attachments (PDF or Image)'
              inputProps={{ multiple: true, accept: 'image/*,application/pdf' }}
              InputLabelProps={{ shrink: true }}
              autoComplete='file'
              type='file'
              onChange={handleFileChange}
            />
          </Grid>

          {/* Announcement Type Selection */}
          <Grid item xs={12}>
            <FormControl component='fieldset' variant='standard'>
              <FormLabel component='legend' sx={{ fontWeight: 600, mb: 0.5 }}>
                Announcement Type(s)
              </FormLabel>
              <FormGroup row sx={{ gap: 2 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedTypes.includes('NOTICE')}
                      onChange={() => handleTypeToggle('NOTICE')}
                    />
                  }
                  label='NOTICE'
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedTypes.includes('NEWS')}
                      onChange={() => handleTypeToggle('NEWS')}
                    />
                  }
                  label='NEWS'
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedTypes.includes('MEMBER_NOTICE')}
                      onChange={() => handleTypeToggle('MEMBER_NOTICE')}
                    />
                  }
                  label='MEMBER NOTICE'
                />
              </FormGroup>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <Box display='flex' flexWrap='wrap' gap={2} mt={1}>
              {selectedFiles?.map((file, index) =>
                file.type.startsWith('image/') ? (
                  <img
                    key={index}
                    src={URL.createObjectURL(file)}
                    alt={`preview-${index}`}
                    width={50}
                    height={50}
                    style={{
                      borderRadius: 8,
                      objectFit: 'cover',
                      boxShadow: '0 0 5px rgba(0,0,0,0.2)',
                    }}
                  />
                ) : (
                  <Box
                    key={index}
                    sx={{
                      border: '1px solid #ccc',
                      borderRadius: 8,
                      padding: '8px 12px',
                      fontSize: '14px',
                    }}
                  >
                    📄 {file.name}
                  </Box>
                )
              )}
            </Box>
          </Grid>

          <Grid item lg={12}>
            <Box
              sx={{
                border: '2px dashed #ccc',
                borderRadius: 2,
                height: 150,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                position: 'relative',
                overflow: 'hidden',
                '&:hover': { borderColor: 'primary.main' },
              }}
              onClick={() => document.getElementById('fileInput')?.click()}
            >
              {!thumbnail ? (
                <Typography color='textSecondary' align='center'>
                  Click to upload thumbnail
                </Typography>
              ) : (
                <img
                  src={URL.createObjectURL(thumbnail)}
                  alt='thumbnail'
                  style={{
                    width: '300px',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                />
              )}

              <input
                id='fileInput'
                type='file'
                accept='image/*'
                style={{ display: 'none' }}
                onChange={(e) => {
                  if (e.target.files && e.target.files[0]) {
                    setThumbnail(e.target.files[0]);
                  }
                }}
              />
            </Box>
          </Grid>

          <Grid item lg={12}>
            <TextField
              label='Content'
              placeholder='Enter some details about this announcement here..'
              multiline
              {...register('content')}
              rows={5}
              maxRows={10}
              fullWidth
            />
          </Grid>
        </Grid>

        {loading ? (
          <Button
            disabled
            fullWidth
            variant='contained'
            disableElevation
            sx={{
              mt: 3,
              mb: 2,
              py: 1,
            }}
          >
            Loading...
          </Button>
        ) : (
          <Button
            type='submit'
            fullWidth
            variant='contained'
            disableElevation
            sx={{
              mt: 3,
              mb: 2,
              py: 1,
            }}
          >
            Create Announcement
          </Button>
        )}
      </Box>
    </>
  );
};

export default CreateAnnoucement;
